/**
 * Order and groups of the calendar navigation (Outlook calendar groups), stored per user by the platform API.
 * Pure helpers place the calendars; the hook loads and saves the layout optimistically.
 */
import { toast } from '@platform/ui';
import { useCallback, useEffect, useState } from 'react';
import { api, type CalendarInfo } from '../api';
import type { CalendarLayout } from '../api/calendar-extra';

export const EMPTY_LAYOUT: CalendarLayout = { groups: [], order: [] };

export interface RailSections {
  own: CalendarInfo[];
  groups: { id: string; name: string; calendars: CalendarInfo[] }[];
  other: CalendarInfo[];
}

/** Calendars in their saved order (unknown ones keep their place after the ordered ones) and sections. */
export function railSections(calendars: readonly CalendarInfo[], layout: CalendarLayout): RailSections {
  const rank = (c: CalendarInfo) => {
    const i = layout.order.indexOf(c.id);
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
  };
  const sorted = calendars.map((c, i) => ({ c, i })).sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i).map((x) => x.c);
  const groupOf = (id: string) => layout.groups.find((g) => g.calendarIds.includes(id));
  return {
    own: sorted.filter((c) => c.kind === 'own' && !groupOf(c.id)),
    groups: layout.groups.map((g) => ({ id: g.id, name: g.name, calendars: sorted.filter((c) => groupOf(c.id)?.id === g.id) })).filter((g) => g.calendars.length > 0),
    other: sorted.filter((c) => c.kind !== 'own' && !groupOf(c.id)),
  };
}

/** The section list a calendar is in. */
export function sectionOf(sections: RailSections, id: string): CalendarInfo[] {
  return [sections.own, ...sections.groups.map((g) => g.calendars), sections.other].find((list) => list.some((c) => c.id === id)) ?? [];
}

/** Moves a calendar one place up/down inside its section; returns the new layout. */
export function moveCalendar(calendars: readonly CalendarInfo[], layout: CalendarLayout, id: string, direction: -1 | 1): CalendarLayout {
  const sections = railSections(calendars, layout);
  const all = [...sections.own, ...sections.groups.flatMap((g) => g.calendars), ...sections.other].map((c) => c.id);
  const section = sectionOf(sections, id).map((c) => c.id);
  const index = section.indexOf(id);
  const neighbour = section[index + direction];
  if (index < 0 || !neighbour) return layout;
  const a = all.indexOf(id);
  const b = all.indexOf(neighbour);
  [all[a], all[b]] = [all[b] as string, all[a] as string];
  return { ...layout, order: all };
}

/** Puts a calendar into the group with this name (created when new); an empty name takes it out of its group. */
export function groupCalendar(layout: CalendarLayout, id: string, name: string, newId: () => string): CalendarLayout {
  const groups = layout.groups.map((g) => ({ ...g, calendarIds: g.calendarIds.filter((c) => c !== id) }));
  const trimmed = name.trim();
  if (trimmed) {
    const existing = groups.find((g) => g.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) existing.calendarIds.push(id);
    else groups.push({ id: newId(), name: trimmed, calendarIds: [id] });
  }
  return { ...layout, groups: groups.filter((g) => g.calendarIds.length > 0) };
}

export function useCalendarLayout(active: boolean, failedMessage: string) {
  const [layout, setLayout] = useState<CalendarLayout>(EMPTY_LAYOUT);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!active || loaded) return;
    let cancelled = false;
    api.calendarLayout().then(
      (value) => {
        if (cancelled) return;
        setLayout(value);
        setLoaded(true);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [active, loaded]);
  const save = useCallback(
    (next: CalendarLayout) => {
      setLayout(next);
      api.setCalendarLayout(next).catch(() => toast.error(failedMessage));
    },
    [failedMessage],
  );
  return { layout, saveLayout: save };
}
